import * as React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item> & {
    'data-test-id'?: string;
  }
>(({ className, 'data-test-id': dataTestId, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn(
      'border rounded-md shadow-sm mb-3 bg-white dark:bg-neutral-950',
      className
    )}
    data-test-id={dataTestId || 'accordion-item'}
    {...props}
  />
));
AccordionItem.displayName = 'AccordionItem';

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
    'data-test-id'?: string;
  }
>(({ className, children, 'data-test-id': dataTestId, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex" data-test-id="accordion-header">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        'flex flex-1 items-center justify-between py-4 px-4 font-medium rounded-md transition-colors [&[data-state=open]>svg]:rotate-180 hover:bg-gray-50 dark:hover:bg-neutral-900/50',
        className
      )}
      data-test-id={dataTestId || 'accordion-trigger'}
      {...props}
    >
      {children}
      <ChevronDown
        className="h-4 w-4 shrink-0 transition-transform duration-200"
        data-test-id="accordion-chevron"
      />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> & {
    'data-test-id'?: string;
  }
>(({ className, children, 'data-test-id': dataTestId, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
    data-test-id={dataTestId || 'accordion-content'}
    {...props}
  >
    <div className={cn('', className)} data-test-id="accordion-content-wrapper">
      {children}
    </div>
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
